'use client';

import { Anchor, Grid, List, Space, Stack, Text, Title } from '@mantine/core';
import ConstructionIcon from '@material-design-icons/svg/filled/construction.svg';
import InfoIcon from '@material-design-icons/svg/filled/warning.svg';

const UserGuideContent = () => {
  const icon = (
    <InfoIcon style={{ fill: '#ffcb4c', verticalAlign: 'middle' }} />
  ); // EMOJI YELLOW TODO: Make this a prop

  return (
    <Grid.Col
      span={{ base: 12, sm: 8 }}
      id="content"
    >
      <Stack gap="md">
        <Title
          order={2}
          id="creating-a-sway-page"
        >
          <strong>Creating a Sway Page</strong>
        </Title>

        <Title
          order={3}
          id="selecting-a-topic"
        >
          Selecting a Topic
        </Title>
        <Text size="md">
          <strong>The atomic unit of Sway is “Vote With”.</strong> When
          supporters visit your <strong>Sway Page</strong>, the most prominent
          thing they&apos;ll see is “Vote With [you] on [topic]”.
          <p />A <strong>topic</strong> on a Sway Page is the specific issue or
          policy area you champion. You get to define your topic in your own
          words — there&apos;s no one right way to do it. But, consider the
          following as you do:
          <p />
          <List>
            <List.Item>
              If your topic is too broad (eg Vote With Me on Everything), it
              might be hard for people to align with you.
            </List.Item>
            <List.Item>
              If your topic is too narrow (eg Vote With Me on Adding a Crosswalk
              to 10th & Elm St), it will be hard for you to grow a coalition
              (more on that below).
            </List.Item>
          </List>
          <p>
            So how to solve? We suggest making topics that last over many
            elections, but are focused. If you have more than one topic, simply
            make more than one Sway Page.
          </p>
          <List>
            <List.Item>
              To go back to our example, “Everything” could be separate Sway
              Pages on “Education”, “Business Development”, “Public
              Transportation”, etc.
            </List.Item>
            <List.Item>
              {icon} The very narrow “Adding a Crosswalk to 10th & Elm St” could
              become “Street Safety”.
            </List.Item>
          </List>
          <p />
          Choosing your topic is the most important decision you make on a Sway
          Page. Once set, it’s unchangeable. It’s also the only element of your
          Sway Page that is required. Every element below is an optional way to
          make your Sway Page as compelling as possible.
        </Text>

        <Title
          order={3}
          id="writing-a-slogan"
        >
          Writing a Slogan
        </Title>
        <Text size="md">
          Once you’ve selected your topic, your <strong>slogan</strong> makes
          your Sway Page come to life. Think of your slogan as a marketing
          tagline. Use action verbs like “demand”, “make”, “count”, etc. Grab
          our attention and tell us — briefly — why we should care. For example:
          Demand a crosswalk for our kids!
          <br />
          The character limit is 32. Your slogan can be updated at any time.
        </Text>

        <Title
          order={3}
          id="educating-your-supporters"
        >
          Educating Your Supporters
        </Title>
        <Text size="md">
          Your Sway Page serves as a handshake point between you and your
          supporters (ie you give them personalized voting recommendations while
          they give you leverage). Your supporters are willing to click the
          “Vote With Me” button on your Sway Page because they know what you’re
          about. They either have heard from you in person or have a sense of
          your values and interests based on your online activity. Your Sway
          Page allows you to reinforce your bona fides and values, but assume
          that much of your messaging happens elsewhere.
          <p />
          To support your storytelling, your Sway Page provides the opportunity
          to share why your topic is important — both to you and the world.
          <p />
          {icon} Extending the example above, if the crosswalk at 10th and Elm
          St is near an elementary school, tell us about the kids you see
          walking home and why that intersection needs to be safer. Snap a photo
          of the intersection in question. Make us get behind you.
          <p />
          Your Sway Page allows you to make as many sections as you like when
          setting up your page. We suggest balancing a rich narrative with
          something people want to read. If people want more information, you
          can link out to relevant resources.
        </Text>

        <Title
          order={3}
          id="choosing-offices-or-measures"
        >
          Choosing Offices or Measures
        </Title>
        <Text size="md">
          What makes your Sway Page powerful is the ability to link your topic
          to the offices or ballot measures that impact it. These connections
          are not always obvious. In fact, Sway Pages are an incredible way to
          bring topics to politicians that haven’t been on their radar — until
          you came along.
          <p />
          We suggest that you start with the smallest office that could address
          your topic.
          <p />
          {icon}For our Street Safety example, it’s perhaps a local
          councilperson. Since it’s near a school, it might also impact the
          school board. As you gain leverage, you may want to also lobby larger
          offices like mayor. Don’t go too big unless it’s absolutely necessary.
          It’s unlikely that your senator or the president has much to say about
          individual crosswalks.
        </Text>

        <Space h="lg" />
        <Title
          order={2}
          id="building-leverage"
        >
          <strong>Building Leverage</strong>
        </Title>

        <Title
          order={3}
          id="sharing-your-sway-page"
        >
          Sharing Your Sway Page
        </Title>
        <Text size="md">
          Once your Sway Page is ready to share, click the “Share” button at the
          bottom of your page. From there, you will receive a link to be used in
          social sites that, when used, will contain a preview of your Sway
          Page.
          <p />
          You can also share your Sway Page via a custom created QR code. This
          can be a helpful tool if you’re looking for support from neighbors or
          others in your community.
          <p />
          {icon} Eg. You might want to put a sign up near the dangerous
          intersection.
        </Text>

        <Title
          order={3}
          id="encouraging-your-supporters-to-verify"
        >
          Encouraging Your Supporters to Verify
        </Title>
        <Text size="md">
          Support is awesome, but verified support is transformative. Sway will
          remind your supporters to verify their voter registration, but you
          should, too! Let them know how you plan to address your topic and what
          their added leverage might mean.
          <p />
          {icon}For example, if you were planning to go to a city council
          meeting to take about the need for a crosswalk, it’s way more
          impactful to say that you’re speaking on behalf of a group as opposed
          to only yourself.
          <p />
          The group size that matters is captured by your{' '}
          <strong>Sway Score</strong>, or the number of verified voters backing
          you who can vote in an election for an office or measure you’re making
          a recommendation for. The higher the Sway Score, the more leverage you
          have.
        </Text>

        <Space h="lg" />
        <Title
          order={2}
          id="crafting-your-voter-guide"
        >
          <ConstructionIcon style={{ verticalAlign: 'middle' }} />
          <strong>Crafting Your Voter Guide</strong>
        </Title>

        <List>
          <List.Item>
            <Title
              order={3}
              id="creating-your-voter-guide"
            >
              Creating Your Voter Guide
            </Title>
          </List.Item>
          <List.Item>
            <Title
              order={3}
              id="sharing-your-voter-guide"
            >
              Sharing Your Voter Guide
            </Title>
          </List.Item>
        </List>

        <Space h="lg" />
        <Title
          order={2}
          id="supporting-leaders"
        >
          <strong>Supporting Leaders</strong>
        </Title>
        <Title
          order={3}
          id="aligning-on-a-topic"
        >
          Aligning on a Topic
        </Title>
        <Text size="md">
          The atomic action on Sway is “Vote With.” By agreeing to “Vote With” a
          leader, you’re opting to receive ballot recommendations from someone
          you trust. In exchange, you are giving the leader and topic more
          leverage by backing them.
          <p />
          Only choose to “Vote With” leaders you trust on topics you care about.
          Unlike other platforms where it may be educational to follow people
          you disagree with, a “follow” on Sway gives that leader more power —
          and therefore makes it more likely that you’ll see changes in the
          world.
        </Text>
        <Title
          order={3}
          id="strengthening-your-impact"
        >
          Strengthening Your Impact
        </Title>
        <Text size="md">
          Verifying your voter registration is the biggest unlock a supporter
          can make on Sway. You only have to verify your voter registration
          once, after which you will count in the Sway Score on every Sway Page
          that is recommending an office or measure you can vote for.
          <p />
          Verifying your voter registration on Sway is private and safe. We do
          not store any of the information you share to verifying your
          registration, only that you are registered.
        </Text>

        <Title
          order={3}
          id="amplifying-the-movement"
        >
          Amplifying the Movement
        </Title>
        <Text size="md">
          There are two main ways to amplify the movement for the topic you care
          about.
          <p />
          The first is to simply share the Sway Page you supported with your
          network. This could be in-person or online, and remember to use the
          hashtag #VoteWith.
          <p />
          The second is to become a leader yourself. If you make a Sway Page for
          the same topic, you can start collecting Sway for yourself while also
          sharing relevant supporters with the leader you initially followed.
          Pretty cool, right? Making a Sway Page is easy — click{' '}
          <Anchor href="#creating-a-sway-page">Creating a Sway Page</Anchor>.
        </Text>
        <Title
          order={3}
          id="voting-for-change"
        >
          Voting for Change
        </Title>
        <Text size="md">
          Picture this: you support a leader on Sway, they do great work in the
          world, and then you don’t show up for Election Day. All of your
          efforts go down the drain.
          <p />
          Now picture this: you support a leader on Sway, you vote, and now you
          both have even more power to make the world a better place. Much
          better, right?
          <p />
          The most important thing you can do after clicking “Vote With Me” is
          to show up to the ballot box — and with Sway, you’ll be voting your
          values.
        </Text>

        <Space h="lg" />

        <Title
          order={2}
          id="updating-your-profile"
        >
          <strong>Updating Your Profile</strong>
        </Title>

        <Title
          order={3}
          id="choosing-your-profile-photo"
        >
          Choosing Your Profile Photo
        </Title>
        <Text size="md">
          Sway is about trust. Your Sway Page will perform better if you use a
          headshot or a photo that is recognizable from other social accounts.
        </Text>
        <Title
          order={3}
          id="writing-your-headline"
        >
          Writing Your Headline
        </Title>
        <Text size="md">
          Your headline is an opportunity to show your connection to your topic.
          This can be professional or personal — or a combination of both.
          Headlines are limited to 30 characters.
        </Text>
      </Stack>
    </Grid.Col>
  );
};

export { UserGuideContent };
